<template>
  <Teleport to="body">
    <div class="players-backdrop" @click.self="$emit('close')">
      <div class="players-panel" role="dialog" aria-label="Players on this couch">
        <h2>Players on this couch</h2>
        <p class="sub">
          One sign-in covers everyone here. When you join a seat, flip to your name with
          left and right on your controller.
        </p>
        <ul>
          <li v-for="p in list" :key="p.pid">
            <input
              v-model="p.name"
              maxlength="16"
              :aria-label="`Name for ${p.name}`"
              @change="save(p)"
              @keydown.enter="$event.target.blur()"
            >
            <div class="swatches" role="radiogroup" :aria-label="`${p.name}'s colour`">
              <button
                v-for="c in colours"
                :key="c.key"
                type="button"
                class="swatch"
                :class="{ on: p.colour === c.key }"
                :style="{ background: c.fill }"
                :title="c.name"
                :aria-label="c.name"
                :aria-checked="p.colour === c.key"
                role="radio"
                @click="p.colour = c.key; save(p)"
              ></button>
              <CustomSwatch :value="p.colour" :label="`${p.name}'s custom colour`" @pick="p.colour = $event; save(p)" />
            </div>
            <button
              v-if="list.length > 1"
              type="button"
              class="remove"
              :aria-label="`Remove ${p.name}`"
              title="Remove"
              @click="drop(p)"
            >&times;</button>
          </li>
        </ul>
        <div class="actions">
          <button type="button" class="secondary" :disabled="list.length >= 8" @click="add">Add a player</button>
          <button type="button" class="primary" @click="$emit('close')">Done</button>
        </div>
        <p v-if="error" class="error">{{ error }}</p>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { COLOURS } from '@/account/palette.js';
import { savePerson, addPerson, removePerson } from '@/account/account.js';
import CustomSwatch from './CustomSwatch.vue';

export default {
  name: 'PlayersPanel',
  components: { CustomSwatch },
  props: {
    uid: { type: String, required: true },
    people: { type: Array, required: true },
  },
  emits: ['close', 'changed'],
  data() {
    return { list: this.people.map((p) => ({ ...p })), colours: COLOURS, error: '' };
  },
  methods: {
    emit() { this.$emit('changed', this.list.map((p) => ({ ...p }))); },
    async save(p) {
      p.name = (p.name || '').trim().slice(0, 16) || 'Player';
      this.emit();
      try { await savePerson(this.uid, p); } catch { this.error = "Couldn't save - check the connection."; }
    },
    async add() {
      try {
        const person = await addPerson(this.uid, this.list);
        this.list.push(person);
        this.emit();
      } catch { this.error = "Couldn't add a player - check the connection."; }
    },
    async drop(p) {
      this.list = this.list.filter((x) => x.pid !== p.pid);
      this.emit();
      try { await removePerson(this.uid, p.pid); } catch { this.error = "Couldn't remove - check the connection."; }
    },
  },
};
</script>

<style scoped>
.players-backdrop {
  position: fixed; inset: 0; z-index: 900; background: rgba(0, 0, 0, 0.6);
  display: grid; place-items: center; padding: 16px;
}
.players-panel {
  width: min(560px, 100%); max-height: 88vh; overflow-y: auto; background: #1a1d29;
  border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 16px; padding: 1.25rem; color: #eef0f6;
}
h2 { margin: 0 0 0.3rem; font-size: 1.4rem; }
.sub { margin: 0 0 1rem; opacity: 0.7; font-size: 0.9rem; }
ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.75rem; }
li { display: grid; grid-template-columns: minmax(0, 9rem) 1fr auto; gap: 0.6rem; align-items: center; }
@media (max-width: 520px) { li { grid-template-columns: 1fr auto; } .swatches { grid-column: 1 / -1; } }
input {
  font: inherit; font-weight: 700; background: #12141c; color: #eef0f6; min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.25); border-radius: 8px; padding: 0.45rem 0.6rem;
}
.swatches { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; }
.swatch {
  width: 26px; height: 26px; border-radius: 50%; border: 2px solid transparent; padding: 0;
}
.swatch.on { border-color: #fff; box-shadow: 0 0 0 2px #12141c inset; }
.remove {
  background: none; border: 0; color: #eef0f6; opacity: 0.5; font-size: 1.4rem; line-height: 1;
}
.actions { display: flex; justify-content: space-between; margin-top: 1.25rem; }
.error { color: #ff8f84; font-size: 0.9rem; margin: 0.75rem 0 0; }
</style>
